import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient, useQuery } from '@apollo/client'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import RangeSlider from './RangeSlider'
import { COLLECTION_COLLECTORS, COLLECTION_DRIVER_TAGS } from './queries'

const RETRIES_OPTIONS = [
    { value: '', label: 'Any' },
    { value: 'false', label: 'Open' },
    { value: 'true', label: 'Exhausted' },
]

const BOOLEAN_OPTIONS = [
    { value: '', label: 'Any' },
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
]

const CAR_STATUS_OPTIONS = [
    { value: 'Enabled', label: 'Enabled' },
    { value: 'Disabled', label: 'Disabled' },
    { value: 'Repo', label: 'Repo' },
]

const WEEKDAYS = [
    { value: 0, label: 'Mon' },
    { value: 1, label: 'Tue' },
    { value: 2, label: 'Wed' },
    { value: 3, label: 'Thu' },
    { value: 4, label: 'Fri' },
    { value: 5, label: 'Sat' },
    { value: 6, label: 'Sun' },
]

const DRIVER_STAGES = [
    { value: -3, label: 'Unknown' },
    { value: -1, label: 'Denied' },
    { value: 0, label: "Didn't take a car" },
    { value: 1, label: 'Applied' },
    { value: 3, label: 'Insurance Approved' },
    { value: 5, label: 'Awaiting Approval' },
    { value: 6, label: 'Active' },
    { value: 10, label: 'Has Rented' },
]

const OPENED_PRESETS = [
    { value: 1, label: 'Today' },
    { value: 7, label: 'Last 7d' },
    { value: 30, label: 'Last 30d' },
    { value: 90, label: 'Last 90d' },
]

const SELECT_STYLES = {
    control: (base, state) => ({
        ...base,
        minHeight: 36,
        borderRadius: 8,
        borderColor: state.isFocused ? '#0f5132' : '#d6dae1',
        boxShadow: state.isFocused
            ? '0 0 0 3px rgba(15, 81, 50, 0.08)'
            : 'none',
        '&:hover': { borderColor: '#0f5132' },
        fontSize: 13,
    }),
    multiValue: (base) => ({
        ...base,
        background: '#d1fae5',
        borderRadius: 6,
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: '#0f5132',
        fontWeight: 500,
        fontSize: 11,
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: '#0f5132',
        ':hover': { background: '#a7f3d0', color: '#064e3b' },
    }),
    placeholder: (base) => ({ ...base, fontSize: 13, color: '#9ca3af' }),
    menu: (base) => ({ ...base, zIndex: 50 }),
    option: (base, state) => ({
        ...base,
        fontSize: 13,
        background: state.isSelected
            ? '#0f5132'
            : state.isFocused
            ? '#f1f3f7'
            : 'transparent',
        color: state.isSelected ? '#fff' : '#1f2937',
    }),
}

function Section({ title, children }) {
    return (
        <div className="cd-filtersection">
            <div className="cd-filtersection-title">{title}</div>
            {children}
        </div>
    )
}

function Segmented({ options, value, onChange, ariaLabel }) {
    return (
        <div className="cd-segmented" role="radiogroup" aria-label={ariaLabel}>
            {options.map((o) => (
                <button
                    key={String(o.value)}
                    type="button"
                    role="radio"
                    aria-checked={value === o.value}
                    className={value === o.value ? 'active' : ''}
                    onClick={() => onChange(o.value)}
                >
                    {o.label}
                </button>
            ))}
        </div>
    )
}

function WeekdayPicker({ value, onChange }) {
    return (
        <div className="cd-weekday-grid">
            {WEEKDAYS.map((d) => (
                <button
                    key={d.value}
                    type="button"
                    role="radio"
                    aria-checked={value === d.value}
                    className={value === d.value ? 'active' : ''}
                    onClick={() => onChange(value === d.value ? '' : d.value)}
                >
                    {d.label}
                </button>
            ))}
            {value !== '' && value != null && (
                <button type="button" className="clear" onClick={() => onChange('')}>
                    Clear day
                </button>
            )}
        </div>
    )
}

function userLabel(user) {
    if (!user) return 'Collector'
    return user.name
        || [user.firstName, user.lastName].filter(Boolean).join(' ')
        || user.username
        || 'Collector'
}

function collectorOption(user) {
    if (!user?.id) return null
    return {
        value: user.id,
        label: userLabel(user),
    }
}

function selectedOptions(options, values) {
    const selectedValues = Array.isArray(values) ? values : []
    return options.filter((opt) => selectedValues.includes(opt.value))
}

export default function FilterPopover({ open, anchorRef, value, onApply, onClose, branches = [] }) {
    const apolloClient = useApolloClient()
    const popoverRef = useRef(null)
    const [draft, setDraft] = useState(value)

    const { data: tagsData, loading: tagsLoading } = useQuery(COLLECTION_DRIVER_TAGS, {
        skip: !open,
        fetchPolicy: 'cache-and-network',
    })
    const { data: collectorsData, loading: collectorsLoading } = useQuery(COLLECTION_COLLECTORS, {
        skip: !open,
        variables: { first: 20 },
        fetchPolicy: 'cache-and-network',
    })

    useEffect(() => {
        if (open) setDraft(value)
    }, [open, value])

    useEffect(() => {
        if (!open) return
        function onDocClick(e) {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(e.target) &&
                anchorRef.current &&
                !anchorRef.current.contains(e.target)
            ) {
                onClose()
            }
        }
        function onKey(e) {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('mousedown', onDocClick)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onDocClick)
            document.removeEventListener('keydown', onKey)
        }
    }, [open, onClose, anchorRef])

    const branchOptions = useMemo(
        () => branches.map((branch) => ({
            value: branch.pk,
            label: branch.name,
        })),
        [branches],
    )
    const tagOptions = useMemo(
        () => (tagsData?.allTags?.edges ?? []).map(({ node }) => ({
            value: node.name,
            label: node.name,
        })),
        [tagsData],
    )
    const collectorDefaultOptions = useMemo(
        () => (collectorsData?.allReps?.edges ?? [])
            .map(({ node }) => collectorOption(node))
            .filter(Boolean),
        [collectorsData],
    )
    const selectedCollectorOptions = useMemo(() => {
        const selectedValues = Array.isArray(draft.assignedToIds) ? draft.assignedToIds : []
        const byValue = new Map(collectorDefaultOptions.map((opt) => [opt.value, opt]))
        return selectedValues.map((id) => byValue.get(id) || { value: id, label: 'Selected collector' })
    }, [collectorDefaultOptions, draft.assignedToIds])

    const loadCollectorOptions = useCallback(async (inputValue) => {
        const result = await apolloClient.query({
            query: COLLECTION_COLLECTORS,
            variables: {
                searchTerm: inputValue?.trim() || undefined,
                first: 20,
            },
            fetchPolicy: 'network-only',
        })
        return (result?.data?.allReps?.edges ?? [])
            .map(({ node }) => collectorOption(node))
            .filter(Boolean)
    }, [apolloClient])

    const update = (key, v) => setDraft((d) => ({ ...d, [key]: v }))

    const apply = () => {
        const clean = { ...draft }
        Object.keys(clean).forEach((k) => {
            if (
                clean[k] === ''
                || clean[k] == null
                || (Array.isArray(clean[k]) && clean[k].length === 0)
            ) {
                delete clean[k]
            }
        })
        onApply(clean)
    }
    const reset = () => {
        setDraft({})
        onApply({})
    }

    const activeCount = Object.values(draft).filter(
        (v) => v !== '' && v != null && (!Array.isArray(v) || v.length > 0),
    ).length

    if (!open) return null

    return (
        <div ref={popoverRef} className="cd-filterpopover" role="dialog" aria-label="Filters">
            <header className="cd-filter-header">
                <span className="cd-filter-title">Filters</span>
                {activeCount > 0 && (
                    <span className="cd-filter-activecount">{activeCount} active</span>
                )}
                <button
                    type="button"
                    className="cd-filter-close"
                    onClick={onClose}
                    aria-label="Close filters"
                >
                    ×
                </button>
            </header>

            <div className="cd-filter-body">
                <Section title="Driver stage">
                    <Select
                        isMulti
                        isClearable
                        placeholder="All stages"
                        options={DRIVER_STAGES}
                        value={DRIVER_STAGES.filter((opt) =>
                            Array.isArray(draft.driverStages)
                            && draft.driverStages.includes(opt.value),
                        )}
                        onChange={(selected) => {
                            const next = (selected || []).map((o) => o.value)
                            update('driverStages', next.length > 0 ? next : '')
                        }}
                        classNamePrefix="cd-select"
                        styles={SELECT_STYLES}
                    />
                </Section>

                <Section title="Branches">
                    <Select
                        isMulti
                        isClearable
                        placeholder="All branches"
                        options={branchOptions}
                        value={selectedOptions(branchOptions, draft.branchIds)}
                        onChange={(selected) => {
                            const next = (selected || []).map((o) => o.value)
                            update('branchIds', next.length > 0 ? next : '')
                        }}
                        classNamePrefix="cd-select"
                        styles={SELECT_STYLES}
                    />
                </Section>

                <Section title="Car status">
                    <Select
                        isMulti
                        isClearable
                        placeholder="All car statuses"
                        options={CAR_STATUS_OPTIONS}
                        value={selectedOptions(CAR_STATUS_OPTIONS, draft.carStatusIn)}
                        onChange={(selected) => {
                            const next = (selected || []).map((o) => o.value)
                            update('carStatusIn', next.length > 0 ? next : '')
                        }}
                        classNamePrefix="cd-select"
                        styles={SELECT_STYLES}
                    />
                </Section>

                <Section title="Driver tags">
                    <Select
                        isMulti
                        isClearable
                        isLoading={tagsLoading}
                        placeholder="All tags"
                        options={tagOptions}
                        value={selectedOptions(tagOptions, draft.driverTags)}
                        onChange={(selected) => {
                            const next = (selected || []).map((o) => o.value)
                            update('driverTags', next.length > 0 ? next : '')
                        }}
                        classNamePrefix="cd-select"
                        styles={SELECT_STYLES}
                    />
                </Section>

                <Section title="Billing day">
                    <WeekdayPicker
                        value={draft.driverBillingDay ?? ''}
                        onChange={(v) => update('driverBillingDay', v)}
                    />
                </Section>

                <Section title="Opening balance">
                    <RangeSlider
                        min={0}
                        max={5000}
                        step={25}
                        low={draft.openingBalanceGte}
                        high={draft.openingBalanceLte}
                        onChange={({ low, high }) => {
                            update('openingBalanceGte', low)
                            update('openingBalanceLte', high)
                        }}
                        formatValue={(v) => `$${Number(v).toLocaleString()}`}
                    />
                </Section>

                <Section title="Opened">
                    <div className="cd-opened-presets" role="radiogroup" aria-label="Opened relative range">
                        {OPENED_PRESETS.map((p) => {
                            const active = Number(draft.openedWithinDays) === p.value
                            return (
                                <button
                                    key={p.value}
                                    type="button"
                                    role="radio"
                                    aria-checked={active}
                                    className={active ? 'active' : ''}
                                    onClick={() => {
                                        if (active) {
                                            update('openedWithinDays', '')
                                        } else {
                                            update('openedWithinDays', p.value)
                                            // Relative range supersedes the absolute fields.
                                            update('openedAtDateGte', '')
                                            update('openedAtDateLte', '')
                                        }
                                    }}
                                >
                                    {p.label}
                                </button>
                            )
                        })}
                    </div>
                    <div className="cd-opened-or">or pick a custom range</div>
                    <div className="cd-filter-daterow">
                        <input
                            type="date"
                            value={draft.openedAtDateGte ?? ''}
                            onChange={(e) => {
                                update('openedAtDateGte', e.target.value)
                                if (e.target.value) update('openedWithinDays', '')
                            }}
                            aria-label="Opened on or after"
                        />
                        <span className="cd-range-dash">→</span>
                        <input
                            type="date"
                            value={draft.openedAtDateLte ?? ''}
                            onChange={(e) => {
                                update('openedAtDateLte', e.target.value)
                                if (e.target.value) update('openedWithinDays', '')
                            }}
                            aria-label="Opened on or before"
                        />
                    </div>
                </Section>

                <Section title="Payment plan">
                    <Segmented
                        ariaLabel="Payment plan"
                        value={draft.hasPaymentPlan ?? ''}
                        onChange={(v) => update('hasPaymentPlan', v)}
                        options={BOOLEAN_OPTIONS}
                    />
                </Section>

                <Section title="Payment scheduled">
                    <Segmented
                        ariaLabel="Payment scheduled"
                        value={draft.hasPaymentSchedule ?? ''}
                        onChange={(v) => update('hasPaymentSchedule', v)}
                        options={BOOLEAN_OPTIONS}
                    />
                </Section>

                <Section title="Driver in office">
                    <Segmented
                        ariaLabel="Driver in office"
                        value={draft.driverInOffice ?? ''}
                        onChange={(v) => update('driverInOffice', v)}
                        options={BOOLEAN_OPTIONS}
                    />
                </Section>

                <Section title="Attempts">
                    <Segmented
                        ariaLabel="Attempts"
                        value={draft.retriesExhausted ?? ''}
                        onChange={(v) => update('retriesExhausted', v)}
                        options={RETRIES_OPTIONS}
                    />
                </Section>

                <Section title="Assigned collector">
                    <AsyncSelect
                        isMulti
                        isClearable
                        cacheOptions
                        defaultOptions={collectorDefaultOptions}
                        loadOptions={loadCollectorOptions}
                        isLoading={collectorsLoading}
                        placeholder="Search collectors"
                        value={selectedCollectorOptions}
                        onChange={(selected) => {
                            const next = (selected || []).map((o) => o.value)
                            update('assignedToIds', next.length > 0 ? next : '')
                        }}
                        classNamePrefix="cd-select"
                        styles={SELECT_STYLES}
                    />
                </Section>

                <Section title="Auto charge">
                    <Segmented
                        ariaLabel="Auto charge"
                        value={draft.autoCharge ?? ''}
                        onChange={(v) => update('autoCharge', v)}
                        options={BOOLEAN_OPTIONS}
                    />
                </Section>
            </div>

            <footer className="cd-filter-footer">
                <button type="button" className="cd-btn outline" onClick={reset}>
                    Reset
                </button>
                <button type="button" className="cd-btn primary" onClick={apply}>
                    Apply filters
                </button>
            </footer>
        </div>
    )
}
